import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styles from "./SearchSelect.module.css";

/** The outcome of one search: the matching items, or a message to show in place of them. */
export type SearchSelectOutcome<TItem> = { kind: "ok"; items: TItem[] } | { kind: "error"; message: string };

/**
 * A text field that searches as you type and offers the matches in a list that floats over the page below it, so the search never
 * pushes the rest of the page down. Choosing a match calls `onSelect` and closes the list.
 *
 * The list is drawn on the document body, not beside the field, so a scrolling panel that holds the field cannot clip it. Inside a modal
 * dialog it is drawn on the dialog instead: the dialog sits in the browser's top layer, so a list on the body would sit beneath it and
 * could not be reached. Fixed positioning still escapes the dialog's own scrolling, since the dialog has no transform.
 *
 * The caller owns the text in the field (`value`), which lets it reset the field after a save.
 */
export function SearchSelect<TItem>({
  label,
  placeholder,
  value,
  onValueChange,
  search,
  itemLabel,
  itemKey,
  onSelect,
  onAlternateChoose,
  autoHighlightFirst = false,
  onEscape,
  disabled = false,
  autoFocus = false,
  debounceMs = 250,
  className,
  actions,
  renderItem,
  minLength = 1,
  footer,
  popupMinWidth,
  popupAlign = "start",
  popupClassName,
  inputRef,
  inputAttributes,
}: {
  /** The accessible name of the field. */
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  /** Runs for the trimmed text, after the debounce. The caller maps its own denied or failed responses to an error outcome. */
  search: (term: string) => Promise<SearchSelectOutcome<TItem>>;
  itemLabel: (item: TItem) => string;
  itemKey: (item: TItem) => string;
  onSelect: (item: TItem) => void;
  /** Called, instead of `onSelect`, when Shift+Enter is pressed on the active item. Shift+Enter does nothing when it is not given. */
  onAlternateChoose?: (item: TItem) => void;
  /** When results arrive the first item is active, so Enter chooses it. Defaults to false: nothing is active until an arrow key is pressed. */
  autoHighlightFirst?: boolean;
  /** Called on Escape when the list is already closed, so the caller can close whatever holds the control. */
  onEscape?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  debounceMs?: number;
  /** Sizing and placement for the wrapper, which the page owns. */
  className?: string;
  /** Controls drawn inside the field's right-hand edge, such as a clear or cancel button. */
  actions?: React.ReactNode;
  /** The content of an option. Defaults to `itemLabel(item)`; the option's accessible name is then whatever this draws. */
  renderItem?: (item: TItem) => ReactNode;
  /** How many characters of trimmed text it takes before the list opens and a search runs. Defaults to 1. */
  minLength?: number;
  /** Drawn under the list, only when there are results. */
  footer?: ReactNode;
  /** A width in pixels the list should have at least when the field is narrower; the list stays on screen. Defaults to the field's width. */
  popupMinWidth?: number;
  /** Which edge of the field the list lines up with when it is wider than the field: `start` (the default) the left, `end` the right. */
  popupAlign?: "start" | "end";
  /** An extra class for the list, so a caller can give it its own surface. The list is drawn on the page body, outside the caller's own elements. */
  popupClassName?: string;
  /** Receives the text input, so the caller can focus it. */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Extra attributes for the text input, such as `aria-keyshortcuts`. The control's own attributes win. */
  inputAttributes?: Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "onKeyDown" | "onBlur" | "role" | "type" | "className">;
}): React.JSX.Element {
  const { t } = useTranslation();
  const baseId = useId();
  const listId = `${baseId}-list`;
  const optionId = (index: number) => `${baseId}-option-${String(index)}`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [result, setResult] = useState<{ term: string; outcome: SearchSelectOutcome<TItem> } | null>(null);
  const searchRef = useRef(search);
  const fieldRef = useRef<HTMLDivElement>(null);
  // Where the field sits on the page, so the list can be placed under it. It is measured when the list opens and while the page moves.
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const term = value.trim();
  const enough = term.length >= Math.max(1, minLength);
  const showList = open && enough;

  useEffect(() => {
    if (!showList) return;
    const measure = () => setAnchor(fieldRef.current?.getBoundingClientRect() ?? null);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [showList]);

  // The search is read through a ref, so an inline `search` prop does not restart the debounce on every render.
  useEffect(() => {
    searchRef.current = search;
  });

  useEffect(() => {
    if (!showList) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void searchRef.current(term).then((outcome) => {
        if (!cancelled) setResult({ term, outcome });
      });
    }, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [showList, term, debounceMs]);

  // A result is only shown for the text that produced it, so a slow answer for an earlier word never appears against a later one.
  const current = showList && result?.term === term ? result.outcome : null;
  const items = current?.kind === "ok" ? current.items : [];
  const highlighted = activeIndex >= 0 && activeIndex < items.length ? activeIndex : autoHighlightFirst && items.length > 0 ? 0 : -1;

  // The row the keyboard is on is kept in view when the list scrolls (the list's own scroller, not the page).
  useEffect(() => {
    if (highlighted < 0) return;
    document.getElementById(`${baseId}-option-${String(highlighted)}`)?.scrollIntoView?.({ block: "nearest" });
  }, [highlighted, baseId]);

  const choose = (item: TItem, handler: (item: TItem) => void = onSelect) => {
    handler(item);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!showList) {
        if (enough) {
          setAnchor(fieldRef.current?.getBoundingClientRect() ?? null);
          setOpen(true);
        }
        return;
      }
      if (items.length === 0) return;
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((highlighted + step + items.length) % items.length);
    } else if (event.key === "Enter") {
      if (showList && highlighted >= 0) {
        event.preventDefault();
        if (!event.shiftKey) {
          choose(items[highlighted]);
        } else if (onAlternateChoose) {
          choose(items[highlighted], onAlternateChoose);
        }
      }
    } else if (event.key === "Escape") {
      if (showList) {
        event.preventDefault();
        setOpen(false);
      } else {
        onEscape?.();
      }
    }
  };

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <div className={styles.field} ref={fieldRef}>
        <input
          {...inputAttributes}
          ref={inputRef}
          type="text"
          role="combobox"
          className={styles.input}
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={showList && items.length > 0 ? listId : undefined}
          aria-activedescendant={highlighted >= 0 ? optionId(highlighted) : undefined}
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(event) => {
            onValueChange(event.target.value);
            setAnchor(fieldRef.current?.getBoundingClientRect() ?? null);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => setOpen(false)}
        />
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {showList && anchor && createPortal(<div className={popupClassName ? `${styles.popup} ${popupClassName}` : styles.popup} style={popupPlacement(anchor, popupMinWidth, popupAlign)}>
        {current === null && <p role="status">{t("searchSelect.searching")}</p>}
        {current?.kind === "error" && <p role="alert">{current.message}</p>}
        {current?.kind === "ok" && items.length === 0 && <p>{t("searchSelect.noResults")}</p>}
        {items.length > 0 && <ul id={listId} role="listbox" aria-label={label}>
          {items.map((item, index) => (
            <li
              key={itemKey(item)}
              id={optionId(index)}
              role="option"
              aria-selected={index === highlighted}
              className={index === highlighted ? styles.optionActive : undefined}
              // Keeps focus in the field while an option is clicked, so the blur above does not close the list first.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(item)}
            >
              {renderItem ? renderItem(item) : itemLabel(item)}
            </li>
          ))}
        </ul>}
        {items.length > 0 && footer && <div className={styles.footer}>{footer}</div>}
      </div>, fieldRef.current?.closest("dialog") ?? document.body)}
    </div>
  );
}

/** Places the list under the field, widened to `minWidth` when the field is narrower and kept inside the window, lined up with the field's left or right edge. */
function popupPlacement(anchor: DOMRect, minWidth: number | undefined, align: "start" | "end"): { top: number; left: number; width: number } {
  const margin = 8;
  const width = minWidth === undefined ? anchor.width : Math.max(anchor.width, Math.min(minWidth, window.innerWidth - 2 * margin));
  const preferred = align === "end" ? anchor.right - width : anchor.left;
  const left = minWidth === undefined ? anchor.left : Math.max(margin, Math.min(preferred, window.innerWidth - width - margin));
  return { top: anchor.bottom + 4, left, width };
}
